import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useDevelopers, useUpsertProject, type Project } from './useInventory';

interface Props {
  open: boolean;
  onClose: () => void;
  project?: Project | undefined;
}

/** Project create/edit (#20), through admin_upsert_project(). */
export function ProjectDialog({ open, onClose, project }: Props) {
  const { t } = useTranslation();
  const { data: developers } = useDevelopers();
  const upsert = useUpsertProject();

  const [developerId, setDeveloperId] = useState('');
  const [name, setName] = useState('');
  const [area, setArea] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDeveloperId(project?.developer_id ?? '');
    setName(project?.name ?? '');
    setArea(project?.area ?? '');
    setDeliveryDate(project?.delivery_date ?? '');
    setError(null);
  }, [open, project]);

  const submit = async () => {
    if (!developerId || !name.trim()) {
      setError(t('inventory.developerAndNameRequired'));
      return;
    }
    try {
      await upsert.mutateAsync({
        id: project?.id ?? null,
        developerId,
        name: name.trim(),
        area: area.trim() || null,
        deliveryDate: deliveryDate || null,
      });
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('common.error'));
    }
  };

  return (
    <Modal
      open={open}
      title={project ? t('inventory.editProject') : t('inventory.addProject')}
      onClose={onClose}
    >
      <div className="flex flex-col gap-3">
        {error ? <Alert tone="error">{error}</Alert> : null}

        <Select
          label={t('inventory.developer')}
          placeholder={t('inventory.chooseDeveloper')}
          value={developerId}
          onChange={(e) => setDeveloperId(e.target.value)}
          options={(developers ?? []).map((d) => ({ value: d.id, label: d.name }))}
        />
        <Input label={t('inventory.name')} value={name} onChange={(e) => setName(e.target.value)} />
        <Input label={t('inventory.area')} value={area} onChange={(e) => setArea(e.target.value)} />
        <Input
          label={t('inventory.deliveryDate')}
          type="date"
          dir="ltr"
          value={deliveryDate}
          onChange={(e) => setDeliveryDate(e.target.value)}
        />

        <div className="mt-2 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button loading={upsert.isPending} onClick={() => void submit()}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
